import { DateTime, Interval } from "luxon";
import { Optional } from "sonobello.utilities.react";

import EnvironmentConfiguration from "../../../constants/EnvironmentConfiguration";
import { DisqualifyingReasonsType } from "../../../dtos/DisqualifyingReason";
import { Opportunity } from "../../../dtos/Opportunity";
import { bodyMassIndex } from "../../../utils/Constants";

interface IPatientHeight {
  inches: number;
  feet: number;
}

/** An object representing the personal information of the user that is interacting with the client. */
export interface IPatient {
  /** The id of the center associated with this patient. */
  centerId: string;
  /** The patient's date of birth. */
  dateOfBirth?: string;
  /** The distance to this patient's selected center. */
  distanceToCenter?: number;
  /** The id of the patient. */
  id: string;
  /** The properly formed email of the patient.
   * @remarks example: `test@gmail.com`
   */
  email?: string;
  /** The first name of the patient.
   * @remarks This prop should not be rendered to the user, see {@link IPatient.displayFirstName}.
   */
  firstName: string;
  /** The height of the patient. */
  height?: IPatientHeight;
  /** The last name of the patient. */
  lastName: string;
  /** The current opportunity associated with the patient. */
  opportunity: Opportunity;
  /** The phone number of the patient.
   * @remarks observes the following template: `9876543210`.
   */
  phoneNumber?: string;
  /** The weight of the patient. */
  weight?: number;
  /** The zip code where the patient is located. */
  zipCode?: string;
  /** Whether the patient is previously qualified.
   * @remarks Defaults to `false`.
   */
  isPreviouslyQualified: boolean;
  /** Timestamp of the previous qualification. */
  qualificationDateUtc?: string;
  /** The first name which should be displayed to the user within the UI. */
  displayFirstName: string;
}

/** Identifies the applicable disqualification given the patient's medical information.
 * @returns {@link DisqualifyingReasonsType} if the patient is disqualified, otherwise `undefined`.
 */
export const evaluatePatientDisqualification = (
  customer: Required<Pick<IPatient, "height" | "weight" | "dateOfBirth">>
): Optional<DisqualifyingReasonsType> => {
  if (
    Interval.fromDateTimes(DateTime.fromISO(customer.dateOfBirth), DateTime.now()).length("years") <
    EnvironmentConfiguration.minimumAge
  )
    return DisqualifyingReasonsType.ObxAge;
  const heightInInches = customer.height.feet * 12 + Number(customer.height.inches);
  const bmi = Number(bodyMassIndex(customer.weight, heightInInches).toFixed(2));
  return bmi <= EnvironmentConfiguration.minimumBodyMassIndex || bmi >= EnvironmentConfiguration.maximumBodyMassIndex
    ? DisqualifyingReasonsType.ObxBmi
    : undefined;
};

export const getBmi = (height: IPatientHeight, weight: number): number => {
  const heightInInches = height.feet * 12 + Number(height.inches);
  return Number(bodyMassIndex(weight, heightInInches).toFixed(2));
};

/** Returns the customer's phone number after formatting it for easy reading. */
export const formatPhoneNumber = (phoneNumber: string): string => {
  return `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6, 10)}`;
};

const unsaltRegex = /^(.*)( \d{4}-\d{2}-\d{2}T\d{6})U?$/;
const unsaltFirstName = (firstName: string): string => {
  const matches = unsaltRegex.exec(firstName);
  if (!matches || !matches[1]) return firstName;
  return matches[1];
};

export class Patient implements IPatient {
  centerId!: string;
  dateOfBirth?: string;
  distanceToCenter?: number;
  id!: string;
  email?: string;
  firstName!: string;
  height?: { inches: number; feet: number };
  lastName!: string;
  opportunity!: Opportunity;
  phoneNumber?: string;
  weight?: number;
  zipCode?: string;
  isPreviouslyQualified = false;
  qualificationDateUtc?: string;
  displayFirstName!: string;

  constructor(patient: Omit<IPatient, "displayFirstName">) {
    this.centerId = patient.centerId;
    this.dateOfBirth = patient.dateOfBirth;
    this.distanceToCenter = patient.distanceToCenter ?? undefined;
    this.id = patient.id;
    this.email = patient.email;
    this.firstName = patient.firstName;
    this.height = patient.height;
    this.lastName = patient.lastName;
    this.opportunity = patient.opportunity;
    this.phoneNumber = patient.phoneNumber;
    this.weight = patient.weight;
    this.zipCode = patient.zipCode;
    this.isPreviouslyQualified = patient.isPreviouslyQualified;
    this.qualificationDateUtc = patient.qualificationDateUtc;

    this.displayFirstName = unsaltFirstName(patient.firstName);
  }
}
